/**
 * Etrenzik Design — question sets, size bands, and sizing rules for the
 * verticals that ship from the compact table at the bottom of `verticals.ts`.
 *
 * This file extends that content model rather than rewriting it. Every question
 * is written for someone who owns a business, not someone who designs
 * buildings: no "massing", no "gross square feet", no "program". At least one
 * question per vertical drives square footage.
 *
 * Every number here is a Southeast US planning rule of thumb. They exist to put
 * an owner in the right range, not to replace a design professional, and they
 * are surfaced as ranges everywhere they appear.
 */

import type { Question, SizeTranche } from './verticals';

export interface DerivedSize {
  low: number;
  high: number;
  basis: string;
}

export interface VerticalDetail {
  questions: Question[];
  /** Only when the generic bands would mislead for this building type. */
  tranches?: SizeTranche[];
  deriveSf?: (a: Record<string, unknown>) => DerivedSize | null;
}

// ── helpers ────────────────────────────────────────────────────────────────

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

const has = (v: unknown, key: string): boolean => list(v).includes(key);

/**
 * The narrowest band we will ever show.
 *
 * Some verticals derive from a figure the customer types in directly — square
 * feet of production floor, bays times bay size, units times unit size — so the
 * arithmetic comes out tight. Reporting 43,000-46,000 sf off one typed number
 * implies a precision that does not exist at concept stage, and it invites an
 * owner to treat it as a budget. Anything narrower than this gets widened around
 * its midpoint.
 */
const MIN_SPREAD = 1.25;

/** Round to a sensible increment so we never imply precision we do not have. */
const band = (low: number, high: number, basis: string, to = 500): DerivedSize => {
  let lo = low;
  let hi = high;
  if (hi / lo < MIN_SPREAD) {
    const mid = (lo + hi) / 2;
    lo = mid / Math.sqrt(MIN_SPREAD);
    hi = mid * Math.sqrt(MIN_SPREAD);
  }
  return {
    low: Math.max(to, Math.round(lo / to) * to),
    high: Math.max(to * 2, Math.round(hi / to) * to),
    basis,
  };
};

const t = (
  id: string,
  label: string,
  minSf: number,
  maxSf: number,
  anchor: string,
): SizeTranche => ({ id, label, minSf, maxSf, anchor });

/** Asked of most verticals, worded for the building type in question. */
const look = (options: { value: string; label: string }[]): Question => ({
  id: 'exterior_read',
  label: 'When someone drives up, what should it feel like?',
  type: 'select',
  options,
});

// ── the sets ───────────────────────────────────────────────────────────────

export const VERTICAL_DETAIL: Record<string, VerticalDetail> = {
  medical: {
    tranches: [
      t('m1', 'Under 5,000 sf', 0, 5000, 'A small practice, four to eight exam rooms'),
      t('m2', '5,000 – 12,000 sf', 5000, 12000, 'A full clinic or urgent care'),
      t('m3', '12,000 – 30,000 sf', 12000, 30000, 'A multi-specialty group or surgery center'),
      t('m4', '30,000 – 80,000 sf', 30000, 80000, 'A medical office building'),
      t('m5', 'Over 80,000 sf', 80000, 300000, 'A hospital outpatient campus'),
    ],
    questions: [
      {
        id: 'practice_type',
        label: 'What kind of practice is this?',
        type: 'select',
        required: true,
        options: [
          { value: 'primary', label: 'Primary care or family practice' },
          { value: 'specialty', label: 'Specialty clinic' },
          { value: 'urgent', label: 'Urgent care or walk-in' },
          { value: 'dental', label: 'Dental or orthodontic' },
          { value: 'imaging', label: 'Imaging center' },
          { value: 'surgery', label: 'Outpatient surgery center' },
          { value: 'mob', label: 'Multi-tenant medical office building' },
        ],
      },
      {
        id: 'exam_rooms',
        label: 'How many exam rooms, operatories, or treatment bays?',
        type: 'number',
        min: 1,
        max: 300,
        step: 1,
        unit: 'rooms',
        required: true,
        help: 'Count every room where a patient is seen. This is the number that sets the size of the building.',
      },
      {
        id: 'providers',
        label: 'How many providers see patients at the same time?',
        type: 'number',
        min: 1,
        max: 100,
        step: 1,
        unit: 'providers',
        help: 'Doctors, dentists, or nurse practitioners working at once — not total headcount.',
      },
      {
        id: 'procedures',
        label: 'Do you do procedures on site?',
        type: 'select',
        help: 'Operating rooms need medical gas, backup power, and far more air handling than exam rooms.',
        options: [
          { value: 'none', label: 'Exams and consults only' },
          { value: 'minor', label: 'Minor procedures, in the exam rooms' },
          { value: 'proc-room', label: 'A dedicated procedure room' },
          { value: 'or', label: 'Full operating rooms' },
        ],
      },
      {
        id: 'equipment',
        label: 'What equipment goes in the building?',
        type: 'multi',
        help: 'Heavy imaging needs shielding, structural support, and a much larger electrical service.',
        options: [
          { value: 'xray', label: 'X-ray' },
          { value: 'ct', label: 'CT' },
          { value: 'mri', label: 'MRI' },
          { value: 'ultrasound', label: 'Ultrasound' },
          { value: 'mammography', label: 'Mammography' },
          { value: 'cbct', label: 'Dental CBCT' },
          { value: 'lab', label: 'On-site lab' },
          { value: 'none', label: 'None of these yet' },
        ],
      },
      {
        id: 'support_spaces',
        label: 'What else does the building need?',
        type: 'multi',
        options: [
          { value: 'sick-entrance', label: 'A separate entrance for sick patients' },
          { value: 'infusion', label: 'Infusion or treatment bays' },
          { value: 'pharmacy', label: 'In-house pharmacy' },
          { value: 'pt', label: 'Physical therapy space' },
          { value: 'after-hours', label: 'After-hours entrance' },
          { value: 'conference', label: 'Conference or teaching space' },
        ],
      },
      look([
        { value: 'clinical', label: 'Clean and clinical — clearly a medical building' },
        { value: 'residential', label: 'Warm and residential, less institutional' },
        { value: 'retail', label: 'Retail storefront, easy to spot from the road' },
        { value: 'campus', label: 'Part of a hospital or medical campus' },
      ]),
    ],
    deriveSf: (a) => {
      const rooms = num(a.exam_rooms);
      if (!rooms) return null;
      const per: Record<string, [number, number]> = {
        primary: [500, 700],
        specialty: [550, 780],
        urgent: [600, 820],
        dental: [350, 500],
        imaging: [900, 1300],
        surgery: [1200, 1800],
        mob: [550, 800],
      };
      const [lo, hi] = per[String(a.practice_type)] ?? [550, 800];
      let low = rooms * lo;
      let high = rooms * hi;

      const eq = a.equipment;
      const adders: [string, number, number][] = [
        ['ct', 500, 800],
        ['mri', 900, 1500],
        ['xray', 200, 350],
        ['mammography', 200, 350],
        ['lab', 300, 600],
      ];
      for (const [key, l, h] of adders) {
        if (has(eq, key)) {
          low += l;
          high += h;
        }
      }
      if (has(a.support_spaces, 'pt')) {
        low += 900;
        high += 1600;
      }
      if (has(a.support_spaces, 'infusion')) {
        low += 500;
        high += 1000;
      }
      if (has(a.support_spaces, 'pharmacy')) {
        low += 350;
        high += 700;
      }
      if (a.procedures === 'or') {
        low *= 1.15;
        high *= 1.3;
      }

      const word = a.practice_type === 'dental' ? 'operatories' : 'exam rooms';
      return band(low, high, `${rooms} ${word}, plus waiting, support, and staff space.`, 250);
    },
  },

  education: {
    tranches: [
      t('e1', 'Under 15,000 sf', 0, 15000, 'An addition, a gym, or a small academy'),
      t('e2', '15,000 – 45,000 sf', 15000, 45000, 'A small elementary school'),
      t('e3', '45,000 – 90,000 sf', 45000, 90000, 'A full elementary or middle school'),
      t('e4', '90,000 – 180,000 sf', 90000, 180000, 'A high school'),
      t('e5', 'Over 180,000 sf', 180000, 500000, 'A large high school or campus'),
    ],
    questions: [
      {
        id: 'school_level',
        label: 'Who is the building for?',
        type: 'select',
        required: true,
        options: [
          { value: 'early', label: 'Pre-K and kindergarten' },
          { value: 'elementary', label: 'Elementary' },
          { value: 'middle', label: 'Middle school' },
          { value: 'high', label: 'High school' },
          { value: 'career', label: 'Career, technical, or trade academy' },
          { value: 'higher', label: 'College or university' },
        ],
      },
      {
        id: 'students',
        label: 'How many students will it hold?',
        type: 'number',
        min: 25,
        max: 5000,
        step: 25,
        unit: 'students',
        required: true,
        help: 'Design capacity, not today’s enrollment. This sets the size of almost everything else.',
      },
      {
        id: 'project_kind',
        label: 'Is this a new building or a change to one you have?',
        type: 'select',
        options: [
          { value: 'new', label: 'A new building' },
          { value: 'addition', label: 'An addition to an existing school' },
          { value: 'replace', label: 'Replacing a building on the same campus' },
          { value: 'reuse', label: 'Converting a building we already own' },
        ],
      },
      {
        id: 'shared_spaces',
        label: 'Which of these are in the building?',
        type: 'multi',
        help: 'These are the big rooms, and they drive both the size and the structure.',
        options: [
          { value: 'gym', label: 'Gymnasium' },
          { value: 'cafeteria', label: 'Cafeteria and kitchen' },
          { value: 'auditorium', label: 'Auditorium or performance space' },
          { value: 'media', label: 'Media center or library' },
          { value: 'labs', label: 'Science labs' },
          { value: 'shops', label: 'Shop or career-tech labs' },
          { value: 'athletics', label: 'Locker rooms and athletics' },
          { value: 'admin', label: 'Administration and counseling' },
        ],
      },
      {
        id: 'security',
        label: 'How should people get in?',
        type: 'select',
        help: 'Single-point entry with a secure vestibule is now standard, and it shapes the front of the building.',
        options: [
          { value: 'vestibule', label: 'One secure entry vestibule through the office' },
          { value: 'controlled', label: 'Controlled entry with a staffed desk' },
          { value: 'open-campus', label: 'Open campus with separate buildings' },
        ],
      },
      look([
        { value: 'civic', label: 'Civic and dignified, a landmark for the community' },
        { value: 'warm', label: 'Warm and welcoming, scaled to children' },
        { value: 'contemporary', label: 'Contemporary and forward-looking' },
        { value: 'traditional', label: 'Traditional brick, like the schools around it' },
      ]),
    ],
    deriveSf: (a) => {
      const students = num(a.students);
      if (!students) return null;
      const per: Record<string, [number, number]> = {
        early: [110, 150],
        elementary: [130, 175],
        middle: [150, 200],
        high: [170, 230],
        career: [180, 260],
        higher: [180, 260],
      };
      const [lo, hi] = per[String(a.school_level)] ?? [140, 200];
      let low = students * lo;
      let high = students * hi;
      // An addition is a slice of a school, not a school.
      if (a.project_kind === 'addition') {
        low *= 0.45;
        high *= 0.6;
      }
      const big = list(a.shared_spaces).filter((s) =>
        ['gym', 'cafeteria', 'auditorium', 'shops', 'athletics'].includes(s),
      ).length;
      low *= 1 + big * 0.03;
      high *= 1 + big * 0.05;
      return band(low, high, `${students.toLocaleString()} students, including shared and support space.`, 1000);
    },
  },

  municipal: {
    tranches: [
      t('mu1', 'Under 8,000 sf', 0, 8000, 'A branch library or a two-bay fire station'),
      t('mu2', '8,000 – 20,000 sf', 8000, 20000, 'A city hall or police station for a small city'),
      t('mu3', '20,000 – 45,000 sf', 20000, 45000, 'A public safety complex or main library'),
      t('mu4', '45,000 – 100,000 sf', 45000, 100000, 'A county government center'),
      t('mu5', 'Over 100,000 sf', 100000, 400000, 'A courthouse or civic campus'),
    ],
    questions: [
      {
        id: 'facility_kind',
        label: 'What kind of building is this?',
        type: 'select',
        required: true,
        options: [
          { value: 'city-hall', label: 'City hall or administration' },
          { value: 'police', label: 'Police or sheriff' },
          { value: 'fire', label: 'Fire station' },
          { value: 'courthouse', label: 'Courthouse or judicial' },
          { value: 'library', label: 'Library' },
          { value: 'public-works', label: 'Public works or fleet' },
          { value: 'community', label: 'Community or senior center' },
        ],
      },
      {
        id: 'apparatus_bays',
        label: 'How many apparatus bays?',
        type: 'number',
        min: 1,
        max: 12,
        step: 1,
        unit: 'bays',
        help: 'Drive-through bays are larger than back-in bays and cost more, but they are much safer.',
        showIf: { questionId: 'facility_kind', equals: 'fire' },
      },
      {
        id: 'staff',
        label: 'How many people work in the building?',
        type: 'number',
        min: 2,
        max: 1000,
        step: 5,
        unit: 'staff',
        help: 'On the largest shift, not the total payroll.',
      },
      {
        id: 'public_spaces',
        label: 'What does the public use?',
        type: 'multi',
        options: [
          { value: 'council', label: 'Council or courtroom' },
          { value: 'lobby', label: 'Service counters and lobby' },
          { value: 'meeting', label: 'Public meeting rooms' },
          { value: 'records', label: 'Records and clerk' },
          { value: 'none', label: 'Staff only, no public access' },
        ],
      },
      {
        id: 'operations',
        label: 'Does anything here run around the clock?',
        type: 'multi',
        help: 'Continuous operations drive backup power, bunk and decontamination space, and a separate HVAC approach.',
        options: [
          { value: 'sleeping', label: 'Crews sleep on site' },
          { value: 'dispatch', label: 'Dispatch or emergency operations' },
          { value: 'holding', label: 'Holding cells or secure custody' },
          { value: 'decon', label: 'Decontamination and turnout gear' },
          { value: 'generator', label: 'Must stay open in a power outage' },
          { value: 'none', label: 'Normal business hours' },
        ],
      },
      look([
        { value: 'classical', label: 'Traditional civic, columns and masonry' },
        { value: 'contemporary-civic', label: 'Contemporary but clearly public' },
        { value: 'approachable', label: 'Approachable and open, not intimidating' },
        { value: 'utilitarian', label: 'Straightforward and hard-working' },
      ]),
    ],
    deriveSf: (a) => {
      const kind = String(a.facility_kind);
      const staff = num(a.staff);

      if (kind === 'fire') {
        const bays = num(a.apparatus_bays);
        if (!bays) return null;
        const drive = 1600;
        const support = 4000 + (staff ?? 6) * 180;
        const low = bays * drive + support * 0.85;
        const high = bays * 2400 + support * 1.35;
        return band(low, high, `${bays} apparatus ${bays === 1 ? 'bay' : 'bays'}, plus living, support, and decontamination space.`);
      }

      if (!staff) return null;
      const per: Record<string, [number, number]> = {
        'city-hall': [260, 380],
        police: [350, 550],
        courthouse: [450, 700],
        library: [400, 700],
        'public-works': [300, 500],
        community: [350, 600],
      };
      const [lo, hi] = per[kind] ?? [300, 480];
      let low = staff * lo;
      let high = staff * hi;
      if (has(a.public_spaces, 'council')) {
        low += 1800;
        high += 3500;
      }
      if (has(a.operations, 'holding')) {
        low += 1200;
        high += 2500;
      }
      return band(low, high, `${staff} staff, plus public space and support.`);
    },
  },

  manufacturing: {
    tranches: [
      t('mf1', 'Under 15,000 sf', 0, 15000, 'A fabrication or machine shop'),
      t('mf2', '15,000 – 50,000 sf', 15000, 50000, 'A single production line with offices'),
      t('mf3', '50,000 – 150,000 sf', 50000, 150000, 'A multi-line plant'),
      t('mf4', '150,000 – 400,000 sf', 150000, 400000, 'A large manufacturing facility'),
      t('mf5', 'Over 400,000 sf', 400000, 1500000, 'A major plant or campus'),
    ],
    questions: [
      {
        id: 'production_type',
        label: 'What happens in the building?',
        type: 'select',
        required: true,
        options: [
          { value: 'fabrication', label: 'Metal fabrication or machining' },
          { value: 'assembly', label: 'Assembly of finished products' },
          { value: 'food', label: 'Food or beverage production' },
          { value: 'process', label: 'Chemical or process manufacturing' },
          { value: 'wood', label: 'Millwork, cabinetry, or wood products' },
          { value: 'plastics', label: 'Plastics, molding, or extrusion' },
          { value: 'other', label: 'Something else' },
        ],
      },
      {
        id: 'production_sf',
        label: 'How much production floor do you need?',
        type: 'number',
        min: 2000,
        max: 1000000,
        step: 1000,
        unit: 'sf',
        required: true,
        help: 'The floor area your equipment and work areas occupy. We will add offices and support to it.',
      },
      {
        id: 'clear_height',
        label: 'How much clear height do you need?',
        type: 'select',
        help: 'Measured to the bottom of the structure, not the peak of the roof.',
        options: [
          { value: '16', label: 'About 16 feet' },
          { value: '20', label: 'About 20 feet' },
          { value: '24', label: 'About 24 feet' },
          { value: '30', label: '30 feet or more' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'crane',
        label: 'Do you need an overhead crane?',
        type: 'select',
        help: 'Cranes drive the structure and the wall height more than almost anything else.',
        options: [
          { value: 'none', label: 'No crane' },
          { value: 'jib', label: 'Jib or small hoists only' },
          { value: 'bridge-5', label: 'Bridge crane up to 5 tons' },
          { value: 'bridge-15', label: 'Bridge crane 5 to 15 tons' },
          { value: 'bridge-heavy', label: 'Heavier than 15 tons' },
        ],
      },
      {
        id: 'office_share',
        label: 'How much office and support space?',
        type: 'select',
        options: [
          { value: 'minimal', label: 'Minimal — a small office and a break room' },
          { value: 'standard', label: 'Standard — offices, break room, locker rooms' },
          { value: 'headquarters', label: 'This is also our headquarters' },
        ],
      },
      {
        id: 'plant_needs',
        label: 'What else does the plant need?',
        type: 'multi',
        options: [
          { value: 'dock', label: 'Dock-high doors for trailers' },
          { value: 'grade', label: 'Drive-in doors at grade' },
          { value: 'heavy-power', label: 'Heavy electrical service' },
          { value: 'compressed-air', label: 'Compressed air throughout' },
          { value: 'dust', label: 'Dust or fume collection' },
          { value: 'clean', label: 'Clean or controlled areas' },
          { value: 'wash', label: 'Washdown areas and floor drains' },
          { value: 'rail', label: 'Rail service' },
        ],
      },
      look([
        { value: 'industrial', label: 'Straightforward industrial' },
        { value: 'corporate', label: 'A corporate front on an industrial building' },
        { value: 'showcase', label: 'A showcase — customers and recruits will visit' },
      ]),
    ],
    deriveSf: (a) => {
      const floor = num(a.production_sf);
      if (!floor) return null;
      const share: Record<string, [number, number]> = {
        minimal: [0.04, 0.08],
        standard: [0.08, 0.15],
        headquarters: [0.18, 0.3],
      };
      const [lo, hi] = share[String(a.office_share)] ?? [0.08, 0.15];
      return band(
        floor * (1 + lo),
        floor * (1 + hi),
        `${floor.toLocaleString()} sf of production floor, plus offices and support.`,
        1000,
      );
    },
  },

  automotive: {
    tranches: [
      t('au1', 'Under 6,000 sf', 0, 6000, 'A quick-lube or a small service shop'),
      t('au2', '6,000 – 15,000 sf', 6000, 15000, 'A tire store, collision shop, or car wash'),
      t('au3', '15,000 – 40,000 sf', 15000, 40000, 'A single-franchise dealership'),
      t('au4', '40,000 – 90,000 sf', 40000, 90000, 'A large dealership with full service'),
      t('au5', 'Over 90,000 sf', 90000, 250000, 'An auto mall or multi-brand campus'),
    ],
    questions: [
      {
        id: 'auto_type',
        label: 'What kind of facility?',
        type: 'select',
        required: true,
        options: [
          { value: 'dealership', label: 'New car dealership' },
          { value: 'used', label: 'Used car sales' },
          { value: 'service', label: 'Service and repair' },
          { value: 'collision', label: 'Collision and body shop' },
          { value: 'quick', label: 'Quick lube or tires' },
          { value: 'carwash', label: 'Car wash' },
          { value: 'fleet', label: 'Fleet or truck maintenance' },
        ],
      },
      {
        id: 'service_bays',
        label: 'How many service bays?',
        type: 'number',
        min: 1,
        max: 120,
        step: 1,
        unit: 'bays',
        required: true,
        help: 'Count lifts and work bays. This is usually the biggest part of the building.',
      },
      {
        id: 'display_vehicles',
        label: 'How many vehicles go inside the showroom?',
        type: 'number',
        min: 0,
        max: 60,
        step: 1,
        unit: 'vehicles',
        help: 'Indoor display only. Lot inventory is a site question, not a building one.',
      },
      {
        id: 'brand_program',
        label: 'Does a manufacturer control how it looks?',
        type: 'select',
        help: 'Most franchise agreements dictate the front elevation, materials, and signage.',
        options: [
          { value: 'strict', label: 'Yes, a full brand image program' },
          { value: 'guidance', label: 'Some guidelines, some freedom' },
          { value: 'none', label: 'No, it is entirely ours' },
        ],
      },
      {
        id: 'auto_needs',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'parts', label: 'Parts department and storage' },
          { value: 'wash', label: 'Wash bay or detail area' },
          { value: 'paint', label: 'Paint booth' },
          { value: 'alignment', label: 'Alignment rack' },
          { value: 'lounge', label: 'Customer lounge and café' },
          { value: 'delivery', label: 'Indoor delivery bay' },
          { value: 'ev', label: 'EV service and charging' },
          { value: 'offices', label: 'Sales offices and finance' },
        ],
      },
      look([
        { value: 'brand', label: 'Whatever the manufacturer requires' },
        { value: 'premium', label: 'Premium and glassy' },
        { value: 'workmanlike', label: 'Clean and workmanlike' },
        { value: 'local', label: 'Local and independent, not a chain' },
      ]),
    ],
    deriveSf: (a) => {
      const bays = num(a.service_bays);
      if (!bays) return null;
      const perBay: Record<string, [number, number]> = {
        collision: [800, 1100],
        quick: [500, 700],
        fleet: [900, 1300],
        carwash: [700, 1100],
      };
      const [lo, hi] = perBay[String(a.auto_type)] ?? [650, 900];
      let low = bays * lo;
      let high = bays * hi;

      const display = num(a.display_vehicles) ?? 0;
      low += display * 900;
      high += display * 1300;

      if (has(a.auto_needs, 'parts')) {
        low += 1500;
        high += 4000;
      }
      if (has(a.auto_needs, 'paint')) {
        low += 800;
        high += 1500;
      }
      if (has(a.auto_needs, 'lounge')) {
        low += 600;
        high += 1400;
      }

      const parts = [`${bays} service ${bays === 1 ? 'bay' : 'bays'}`];
      if (display) parts.push(`${display} indoor display ${display === 1 ? 'vehicle' : 'vehicles'}`);
      return band(low, high, `${parts.join(' and ')}, plus parts, offices, and customer space.`);
    },
  },

  restaurant: {
    tranches: [
      t('r1', 'Under 2,000 sf', 0, 2000, 'A coffee shop or a small takeout counter'),
      t('r2', '2,000 – 3,500 sf', 2000, 3500, 'A quick service restaurant with a drive-thru'),
      t('r3', '3,500 – 6,000 sf', 3500, 6000, 'A full service restaurant'),
      t('r4', '6,000 – 12,000 sf', 6000, 12000, 'A large restaurant, brewery, or taproom'),
      t('r5', 'Over 12,000 sf', 12000, 40000, 'A food hall or a production brewery'),
    ],
    questions: [
      {
        id: 'service_model',
        label: 'What kind of restaurant?',
        type: 'select',
        required: true,
        options: [
          { value: 'qsr', label: 'Quick service with a drive-thru' },
          { value: 'fast-casual', label: 'Fast casual, order at the counter' },
          { value: 'full-service', label: 'Full service, seated and served' },
          { value: 'brewery', label: 'Brewery, taproom, or bar' },
          { value: 'cafe', label: 'Coffee shop or bakery' },
          { value: 'ghost', label: 'Ghost kitchen, delivery only' },
        ],
      },
      {
        id: 'seats',
        label: 'How many seats inside?',
        type: 'number',
        min: 0,
        max: 600,
        step: 5,
        unit: 'seats',
        required: true,
        help: 'Not counting the patio. Enter 0 if it is delivery only. This sets the size of the building.',
      },
      {
        id: 'patio_seats',
        label: 'How many seats outside?',
        type: 'number',
        min: 0,
        max: 400,
        step: 5,
        unit: 'seats',
        help: 'A covered patio changes the roof and the electrical. An open one mostly changes the site.',
      },
      {
        id: 'drive_thru',
        label: 'Do you need a drive-thru?',
        type: 'select',
        help: 'Stacking depth usually drives the site harder than the building does.',
        options: [
          { value: 'none', label: 'No drive-thru' },
          { value: 'single', label: 'One lane' },
          { value: 'dual', label: 'Two lanes that merge' },
          { value: 'pickup', label: 'A pickup window for online orders only' },
        ],
      },
      {
        id: 'kitchen_scale',
        label: 'How much cooking happens here?',
        type: 'select',
        help: 'Hood loads, make-up air, gas service, and grease handling all follow from this.',
        options: [
          { value: 'assembly', label: 'Assembly only — very little cooking' },
          { value: 'standard', label: 'A standard commercial kitchen' },
          { value: 'scratch', label: 'Scratch kitchen with heavy hoods' },
          { value: 'production', label: 'Brewing or food production on site' },
        ],
      },
      {
        id: 'restaurant_extras',
        label: 'What else goes in?',
        type: 'multi',
        options: [
          { value: 'bar', label: 'Full bar' },
          { value: 'private', label: 'Private dining or event room' },
          { value: 'patio-cover', label: 'Covered patio' },
          { value: 'outdoor-bar', label: 'Outdoor bar or stage' },
          { value: 'walkup', label: 'Walk-up window' },
          { value: 'catering', label: 'Catering or commissary space' },
          { value: 'retail', label: 'Retail merchandise area' },
        ],
      },
      look([
        { value: 'brand', label: 'Our brand’s standard building' },
        { value: 'independent', label: 'Independent and local, not a chain' },
        { value: 'industrial', label: 'Industrial and raw' },
        { value: 'hospitality', label: 'Warm and hospitality-forward' },
      ]),
    ],
    deriveSf: (a) => {
      const model = String(a.service_model);
      const seats = Number(a.seats);
      if (model === 'ghost') {
        return band(1500, 3000, 'A delivery-only kitchen with no dining room.', 250);
      }
      if (!Number.isFinite(seats) || seats <= 0) return null;

      const per: Record<string, [number, number]> = {
        qsr: [22, 32],
        'fast-casual': [20, 28],
        'full-service': [28, 38],
        brewery: [34, 48],
        cafe: [20, 30],
      };
      const [lo, hi] = per[model] ?? [24, 34];
      let low = seats * lo;
      let high = seats * hi;

      if (a.kitchen_scale === 'scratch') {
        low *= 1.1;
        high *= 1.2;
      }
      if (a.kitchen_scale === 'production') {
        low *= 1.35;
        high *= 1.6;
      }
      if (a.drive_thru === 'single' || a.drive_thru === 'dual') {
        low += 150;
        high += 350;
      }
      if (has(a.restaurant_extras, 'private')) {
        low += 400;
        high += 900;
      }
      if (has(a.restaurant_extras, 'bar')) {
        low += 250;
        high += 600;
      }

      return band(low, high, `${seats} seats inside, plus the kitchen, restrooms, and back of house.`, 250);
    },
  },

  hospitality: {
    tranches: [
      t('h1', 'Under 30,000 sf', 0, 30000, 'A small event venue or a 40-key inn'),
      t('h2', '30,000 – 60,000 sf', 30000, 60000, 'An 80 to 110 key select service hotel'),
      t('h3', '60,000 – 110,000 sf', 60000, 110000, 'A 120 to 180 key hotel'),
      t('h4', '110,000 – 200,000 sf', 110000, 200000, 'A full service hotel with meeting space'),
      t('h5', 'Over 200,000 sf', 200000, 600000, 'A convention or resort property'),
    ],
    questions: [
      {
        id: 'property_type',
        label: 'What kind of property?',
        type: 'select',
        required: true,
        options: [
          { value: 'limited', label: 'Limited service hotel' },
          { value: 'select', label: 'Select service hotel' },
          { value: 'extended', label: 'Extended stay' },
          { value: 'full', label: 'Full service hotel' },
          { value: 'boutique', label: 'Boutique or independent' },
          { value: 'event', label: 'Event or banquet venue, no rooms' },
        ],
      },
      {
        id: 'keys',
        label: 'How many guest rooms?',
        type: 'number',
        min: 0,
        max: 800,
        step: 5,
        unit: 'rooms',
        required: true,
        help: 'Enter 0 for an event venue with no rooms. Everything else scales from this.',
      },
      {
        id: 'event_capacity',
        label: 'How many people in the largest event space?',
        type: 'number',
        min: 0,
        max: 3000,
        step: 25,
        unit: 'guests',
        help: 'Seated at rounds. Leave at 0 if there is no ballroom or banquet room.',
      },
      {
        id: 'brand_flag',
        label: 'Is it flagged with a brand?',
        type: 'select',
        help: 'A flag comes with prototype drawings and standards that govern much of the design.',
        options: [
          { value: 'flagged', label: 'Yes, with a brand prototype we must follow' },
          { value: 'soft', label: 'A soft brand with some flexibility' },
          { value: 'independent', label: 'Independent' },
          { value: 'undecided', label: 'Not decided yet' },
        ],
      },
      {
        id: 'amenities',
        label: 'What amenities are included?',
        type: 'multi',
        options: [
          { value: 'breakfast', label: 'Breakfast area' },
          { value: 'restaurant', label: 'Restaurant or bar' },
          { value: 'pool-in', label: 'Indoor pool' },
          { value: 'pool-out', label: 'Outdoor pool' },
          { value: 'fitness', label: 'Fitness room' },
          { value: 'meeting', label: 'Meeting rooms' },
          { value: 'laundry', label: 'Guest laundry' },
          { value: 'porte', label: 'Porte-cochere' },
        ],
      },
      look([
        { value: 'brand-standard', label: 'Whatever the brand requires' },
        { value: 'regional', label: 'Regional character, rooted in Georgia' },
        { value: 'contemporary', label: 'Contemporary and crisp' },
        { value: 'boutique-look', label: 'Boutique and distinctive' },
      ]),
    ],
    deriveSf: (a) => {
      const keys = Number(a.keys);
      const guests = Number(a.event_capacity) || 0;
      const type = String(a.property_type);

      if (type === 'event' || (!keys && guests > 0)) {
        if (!guests) return null;
        return band(guests * 22, guests * 32, `${guests} guests seated, plus kitchen, pre-function, and back of house.`);
      }
      if (!Number.isFinite(keys) || keys <= 0) return null;

      const per: Record<string, [number, number]> = {
        limited: [430, 520],
        select: [500, 620],
        extended: [520, 650],
        full: [700, 950],
        boutique: [560, 780],
      };
      const [lo, hi] = per[type] ?? [500, 650];
      let low = keys * lo;
      let high = keys * hi;
      if (guests > 0) {
        low += guests * 18;
        high += guests * 28;
      }
      if (has(a.amenities, 'pool-in')) {
        low += 1800;
        high += 3200;
      }
      if (has(a.amenities, 'restaurant')) {
        low += 1800;
        high += 3500;
      }
      return band(low, high, `${keys} guest rooms, including corridors, lobby, and back of house.`, 1000);
    },
  },

  recreation: {
    tranches: [
      t('rc1', 'Under 10,000 sf', 0, 10000, 'A boutique gym or a single court'),
      t('rc2', '10,000 – 25,000 sf', 10000, 25000, 'A neighborhood fitness club'),
      t('rc3', '25,000 – 60,000 sf', 25000, 60000, 'A two to four court fieldhouse'),
      t('rc4', '60,000 – 120,000 sf', 60000, 120000, 'A community recreation center with aquatics'),
      t('rc5', 'Over 120,000 sf', 120000, 400000, 'A regional sports complex'),
    ],
    questions: [
      {
        id: 'rec_type',
        label: 'What is the building for?',
        type: 'select',
        required: true,
        options: [
          { value: 'fitness', label: 'Fitness club or gym' },
          { value: 'courts', label: 'Basketball or volleyball courts' },
          { value: 'turf', label: 'Indoor turf or fieldhouse' },
          { value: 'racquet', label: 'Pickleball, tennis, or racquet' },
          { value: 'aquatics', label: 'Pool or aquatics center' },
          { value: 'community', label: 'Community recreation center' },
          { value: 'specialty', label: 'Climbing, gymnastics, or skating' },
        ],
      },
      {
        id: 'courts',
        label: 'How many courts or playing surfaces?',
        type: 'number',
        min: 0,
        max: 40,
        step: 1,
        unit: 'courts',
        help: 'A full basketball court with run-out is about 8,000 sf. Volleyball is about half that.',
      },
      {
        id: 'members',
        label: 'How many members or daily visitors?',
        type: 'number',
        min: 0,
        max: 20000,
        step: 50,
        unit: 'people',
        help: 'A rough number is fine. Fitness floors are sized from this.',
      },
      {
        id: 'clear_span',
        label: 'How high does the space need to be?',
        type: 'select',
        options: [
          { value: '18', label: 'About 18 feet — fitness and studios' },
          { value: '24', label: 'About 24 feet — recreational courts' },
          { value: '28', label: '28 feet or more — competition play' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'rec_spaces',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'locker', label: 'Locker rooms and showers' },
          { value: 'track', label: 'Walking or running track' },
          { value: 'studio', label: 'Group fitness studios' },
          { value: 'weights', label: 'Free weights and strength' },
          { value: 'pool', label: 'Pool' },
          { value: 'concession', label: 'Concessions' },
          { value: 'meeting', label: 'Meeting or party rooms' },
          { value: 'childcare', label: 'Childcare or kids’ area' },
          { value: 'offices', label: 'Offices and reception' },
        ],
      },
      look([
        { value: 'energetic', label: 'Energetic and visible from the road' },
        { value: 'civic', label: 'Civic and welcoming to everyone' },
        { value: 'industrial', label: 'Industrial and hard-wearing' },
        { value: 'premium', label: 'Premium and health-club polished' },
      ]),
    ],
    deriveSf: (a) => {
      const courts = Number(a.courts) || 0;
      const members = Number(a.members) || 0;
      const type = String(a.rec_type);

      let low = 0;
      let high = 0;
      const basis: string[] = [];

      if (courts > 0) {
        const perCourt: Record<string, [number, number]> = {
          courts: [7500, 9500],
          racquet: [2800, 3600],
          turf: [16000, 24000],
        };
        const [lo, hi] = perCourt[type] ?? [7500, 9500];
        low += courts * lo;
        high += courts * hi;
        basis.push(`${courts} playing ${courts === 1 ? 'surface' : 'surfaces'}`);
      }

      if (members > 0 && (type === 'fitness' || type === 'community' || courts === 0)) {
        low += members * 8;
        high += members * 13;
        basis.push(`${members.toLocaleString()} members`);
      }

      if (has(a.rec_spaces, 'pool') || type === 'aquatics') {
        low += 8000;
        high += 16000;
        basis.push('an indoor pool');
      }
      if (has(a.rec_spaces, 'track')) {
        low += 3500;
        high += 7000;
      }
      if (has(a.rec_spaces, 'locker')) {
        low += 1800;
        high += 3800;
      }

      if (low === 0) return null;
      return band(low * 1.15, high * 1.3, `${basis.join(', ')}, plus lobby, locker rooms, and support.`, 1000);
    },
  },

  'self-storage': {
    tranches: [
      t('ss1', 'Under 20,000 sf', 0, 20000, 'A small drive-up facility, roughly 150 units'),
      t('ss2', '20,000 – 50,000 sf', 20000, 50000, 'A single-story site of 300 to 450 units'),
      t('ss3', '50,000 – 90,000 sf', 50000, 90000, 'A typical new site, 500 to 700 units'),
      t('ss4', '90,000 – 150,000 sf', 90000, 150000, 'A multi-story climate controlled building'),
      t('ss5', 'Over 150,000 sf', 150000, 400000, 'A large multi-building campus'),
    ],
    questions: [
      {
        id: 'storage_type',
        label: 'What kind of storage?',
        type: 'select',
        required: true,
        options: [
          { value: 'drive-up', label: 'Single-story drive-up' },
          { value: 'climate', label: 'Multi-story climate controlled' },
          { value: 'mixed', label: 'A mix of both' },
          { value: 'boat-rv', label: 'Boat and RV storage' },
          { value: 'contractor', label: 'Contractor or industrial bays' },
        ],
      },
      {
        id: 'units',
        label: 'About how many units do you want?',
        type: 'number',
        min: 20,
        max: 2500,
        step: 10,
        unit: 'units',
        required: true,
        help: 'If you are not sure, most new sites in this market are built around 500 to 700 units.',
      },
      {
        id: 'unit_mix',
        label: 'What sizes will most of them be?',
        type: 'select',
        options: [
          { value: 'small', label: 'Mostly small — 5x5 to 5x10, apartment households' },
          { value: 'mixed', label: 'An even mix' },
          { value: 'large', label: 'Mostly large — 10x20 and up, vehicles and contractors' },
        ],
      },
      {
        id: 'office_model',
        label: 'Will there be an office on site?',
        type: 'select',
        options: [
          { value: 'staffed', label: 'A staffed office and rental counter' },
          { value: 'kiosk', label: 'An unstaffed kiosk' },
          { value: 'remote', label: 'Fully remote, app only' },
        ],
      },
      {
        id: 'site_features',
        label: 'What else does the site need?',
        type: 'multi',
        options: [
          { value: 'gate', label: 'Gated access with keypads' },
          { value: 'cameras', label: 'Camera coverage throughout' },
          { value: 'covered-rv', label: 'Covered RV and boat parking' },
          { value: 'dump', label: 'RV dump station' },
          { value: 'ev', label: 'EV charging' },
          { value: 'elevator', label: 'Freight elevators' },
          { value: 'loading', label: 'Covered loading bays' },
        ],
      },
      look([
        { value: 'commercial', label: 'Clean and commercial, fits a retail corridor' },
        { value: 'residential-buffer', label: 'Softened, because homes are next door' },
        { value: 'utilitarian', label: 'Straightforward and utilitarian' },
        { value: 'flagship', label: 'A flagship building with real street presence' },
      ]),
    ],
    deriveSf: (a) => {
      const units = num(a.units);
      if (!units) return null;
      const avgNra: Record<string, number> = { small: 62, mixed: 100, large: 165 };
      const avg = avgNra[String(a.unit_mix)] ?? 100;
      const efficiency: Record<string, [number, number]> = {
        'drive-up': [0.62, 0.55],
        climate: [0.75, 0.68],
        mixed: [0.68, 0.6],
        'boat-rv': [0.88, 0.8],
        contractor: [0.78, 0.7],
      };
      const [effHigh, effLow] = efficiency[String(a.storage_type)] ?? [0.68, 0.6];
      const nra = units * avg;
      return band(
        nra / effHigh,
        nra / effLow,
        `${units.toLocaleString()} units, about ${Math.round(nra).toLocaleString()} sf rentable, plus hallways and office.`,
        1000,
      );
    },
  },

  multifamily: {
    tranches: [
      t('mfam1', 'Under 25,000 sf', 0, 25000, 'A small infill building, 20 to 25 homes'),
      t('mfam2', '25,000 – 80,000 sf', 25000, 80000, '25 to 80 homes'),
      t('mfam3', '80,000 – 200,000 sf', 80000, 200000, 'A garden community of 80 to 200 homes'),
      t('mfam4', '200,000 – 400,000 sf', 200000, 400000, 'A wrap or podium building'),
      t('mfam5', 'Over 400,000 sf', 400000, 1200000, 'A large multi-building community'),
    ],
    questions: [
      {
        id: 'housing_type',
        label: 'What are you building?',
        type: 'select',
        required: true,
        options: [
          { value: 'garden', label: 'Garden apartments, two or three stories' },
          { value: 'wrap', label: 'Wrap or podium around structured parking' },
          { value: 'townhome', label: 'Townhomes or build-to-rent' },
          { value: 'mixed-use', label: 'Retail below, homes above' },
          { value: 'workforce', label: 'Workforce or affordable housing' },
          { value: 'student', label: 'Student housing' },
        ],
      },
      {
        id: 'units',
        label: 'How many homes?',
        type: 'number',
        min: 4,
        max: 1200,
        step: 4,
        unit: 'homes',
        required: true,
        help: 'Total doors across the whole property.',
      },
      {
        id: 'bedroom_mix',
        label: 'What is the mix?',
        type: 'select',
        options: [
          { value: 'small', label: 'Mostly studios and one-bedrooms' },
          { value: 'balanced', label: 'A balanced mix' },
          { value: 'family', label: 'Mostly two and three-bedrooms' },
        ],
      },
      {
        id: 'parking_model',
        label: 'How does parking work?',
        type: 'select',
        help: 'Structured parking changes the building type, the structure, and the cost more than anything else on this page.',
        options: [
          { value: 'surface', label: 'Surface lots' },
          { value: 'tuck', label: 'Tuck-under or garages at the homes' },
          { value: 'deck', label: 'A parking deck' },
          { value: 'podium', label: 'Parking underneath the building' },
        ],
      },
      {
        id: 'amenities',
        label: 'What amenities are included?',
        type: 'multi',
        options: [
          { value: 'clubhouse', label: 'Clubhouse and leasing office' },
          { value: 'fitness', label: 'Fitness center' },
          { value: 'pool', label: 'Pool' },
          { value: 'coworking', label: 'Coworking or business center' },
          { value: 'dog', label: 'Dog park or pet spa' },
          { value: 'package', label: 'Package room' },
          { value: 'storage', label: 'Resident storage' },
          { value: 'retail', label: 'Ground-floor retail' },
        ],
      },
      look([
        { value: 'traditional', label: 'Traditional Southern, brick and siding' },
        { value: 'contemporary', label: 'Contemporary and urban' },
        { value: 'farmhouse', label: 'Modern farmhouse' },
        { value: 'industrial', label: 'Industrial loft character' },
      ]),
    ],
    deriveSf: (a) => {
      const units = num(a.units);
      if (!units) return null;
      const avg: Record<string, [number, number]> = {
        small: [680, 850],
        balanced: [880, 1050],
        family: [1050, 1300],
      };
      const [lo, hi] = avg[String(a.bedroom_mix)] ?? [880, 1050];
      // Net to gross: corridors, stairs, walls, and amenity space.
      const eff: Record<string, [number, number]> = {
        garden: [0.86, 0.82],
        wrap: [0.84, 0.79],
        townhome: [0.95, 0.9],
        'mixed-use': [0.82, 0.76],
        workforce: [0.86, 0.82],
        student: [0.82, 0.77],
      };
      const [effHigh, effLow] = eff[String(a.housing_type)] ?? [0.85, 0.8];
      let low = (units * lo) / effHigh;
      let high = (units * hi) / effLow;
      if (has(a.amenities, 'clubhouse')) {
        low += 3000;
        high += 7000;
      }
      if (has(a.amenities, 'retail')) {
        low += units * 60;
        high += units * 140;
      }
      return band(low, high, `${units} homes averaging ${lo}–${hi} sf, plus corridors and amenities.`, 1000);
    },
  },

  'senior-living': {
    tranches: [
      t('sl1', 'Under 25,000 sf', 0, 25000, 'A small memory care residence'),
      t('sl2', '25,000 – 60,000 sf', 25000, 60000, 'A 40 to 70 unit assisted living community'),
      t('sl3', '60,000 – 120,000 sf', 60000, 120000, 'A community with several levels of care'),
      t('sl4', '120,000 – 250,000 sf', 120000, 250000, 'A large continuing care campus'),
      t('sl5', 'Over 250,000 sf', 250000, 600000, 'A full life-plan community'),
    ],
    questions: [
      {
        id: 'care_level',
        label: 'What level of care?',
        type: 'select',
        required: true,
        options: [
          { value: 'independent', label: 'Independent living' },
          { value: 'assisted', label: 'Assisted living' },
          { value: 'memory', label: 'Memory care' },
          { value: 'skilled', label: 'Skilled nursing' },
          { value: 'continuum', label: 'A continuum across several levels' },
        ],
      },
      {
        id: 'units',
        label: 'How many residences?',
        type: 'number',
        min: 8,
        max: 600,
        step: 4,
        unit: 'residences',
        required: true,
        help: 'Apartments, suites, or rooms. Everything else scales from this.',
      },
      {
        id: 'dining_model',
        label: 'How does dining work?',
        type: 'select',
        help: 'Restaurant-style service needs more kitchen and more staff than a single seating.',
        options: [
          { value: 'single', label: 'One dining room, set meal times' },
          { value: 'restaurant', label: 'Restaurant-style, open hours' },
          { value: 'multiple', label: 'Several venues including a bistro or private dining' },
          { value: 'inroom', label: 'Mostly in-room dining' },
        ],
      },
      {
        id: 'senior_spaces',
        label: 'What else is in the community?',
        type: 'multi',
        options: [
          { value: 'therapy', label: 'Therapy and rehab gym' },
          { value: 'salon', label: 'Salon and barber' },
          { value: 'theater', label: 'Theater or chapel' },
          { value: 'activity', label: 'Activity and craft rooms' },
          { value: 'wellness', label: 'Wellness clinic' },
          { value: 'courtyard', label: 'Secure courtyard' },
          { value: 'garage', label: 'Resident parking garage' },
        ],
      },
      {
        id: 'resilience',
        label: 'What has to keep working in an outage?',
        type: 'multi',
        help: 'Georgia licensing has expectations here, and it drives the generator and the electrical design.',
        options: [
          { value: 'life-safety', label: 'Life safety and egress only' },
          { value: 'hvac', label: 'Heating and cooling' },
          { value: 'kitchen', label: 'Kitchen and refrigeration' },
          { value: 'whole', label: 'The whole building' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      look([
        { value: 'residential', label: 'Residential, like a large home' },
        { value: 'hospitality', label: 'Hospitality, like a hotel' },
        { value: 'traditional', label: 'Traditional Southern architecture' },
        { value: 'contemporary', label: 'Contemporary and light-filled' },
      ]),
    ],
    deriveSf: (a) => {
      const units = num(a.units);
      if (!units) return null;
      const per: Record<string, [number, number]> = {
        independent: [850, 1150],
        assisted: [650, 900],
        memory: [480, 660],
        skilled: [520, 720],
        continuum: [700, 980],
      };
      const [lo, hi] = per[String(a.care_level)] ?? [650, 900];
      let low = units * lo;
      let high = units * hi;
      if (a.dining_model === 'multiple') {
        low += 1500;
        high += 3500;
      }
      if (has(a.senior_spaces, 'therapy')) {
        low += 900;
        high += 2000;
      }
      if (has(a.senior_spaces, 'theater')) {
        low += 900;
        high += 2000;
      }
      return band(low, high, `${units} residences, including dining, common areas, and staff space.`, 1000);
    },
  },

  childcare: {
    tranches: [
      t('cc1', 'Under 4,000 sf', 0, 4000, 'A small center, roughly 40 children'),
      t('cc2', '4,000 – 8,000 sf', 4000, 8000, 'A center for 60 to 90 children'),
      t('cc3', '8,000 – 14,000 sf', 8000, 14000, 'A center for 100 to 150 children'),
      t('cc4', '14,000 – 25,000 sf', 14000, 25000, 'A large center or preschool'),
      t('cc5', 'Over 25,000 sf', 25000, 60000, 'A multi-program early learning campus'),
    ],
    questions: [
      {
        id: 'capacity',
        label: 'How many children will be licensed for?',
        type: 'number',
        min: 12,
        max: 500,
        step: 6,
        unit: 'children',
        required: true,
        help: 'Georgia sets minimum square feet per child, so this sets the size of the building directly.',
      },
      {
        id: 'age_groups',
        label: 'Which ages will you serve?',
        type: 'multi',
        help: 'Infants need the most space and the most staff per child.',
        options: [
          { value: 'infant', label: 'Infants' },
          { value: 'toddler', label: 'Toddlers' },
          { value: 'preschool', label: 'Preschool' },
          { value: 'prek', label: 'Pre-K' },
          { value: 'school-age', label: 'School age and after-school' },
        ],
      },
      {
        id: 'program_type',
        label: 'What kind of program?',
        type: 'select',
        options: [
          { value: 'daycare', label: 'Full-day childcare' },
          { value: 'preschool', label: 'Preschool or early learning' },
          { value: 'faith', label: 'A ministry of a church' },
          { value: 'employer', label: 'An employer-sponsored center' },
          { value: 'montessori', label: 'Montessori or a specific philosophy' },
        ],
      },
      {
        id: 'childcare_spaces',
        label: 'What else does the building need?',
        type: 'multi',
        options: [
          { value: 'kitchen', label: 'A kitchen that prepares meals' },
          { value: 'gross-motor', label: 'Indoor gross motor room' },
          { value: 'playground', label: 'Playground' },
          { value: 'covered-play', label: 'Covered outdoor play' },
          { value: 'dropoff', label: 'Covered drop-off lane' },
          { value: 'staff', label: 'Staff break and training room' },
          { value: 'admin', label: 'Front office and family meeting room' },
        ],
      },
      {
        id: 'security_model',
        label: 'How do families get in?',
        type: 'select',
        options: [
          { value: 'vestibule', label: 'One controlled entry through the office' },
          { value: 'keypad', label: 'Keypad or fob at a single door' },
          { value: 'classroom', label: 'Parents go directly to classroom doors' },
        ],
      },
      look([
        { value: 'playful', label: 'Playful and colorful' },
        { value: 'homelike', label: 'Homelike and calm' },
        { value: 'natural', label: 'Natural materials and daylight' },
        { value: 'clean', label: 'Clean and professional' },
      ]),
    ],
    deriveSf: (a) => {
      const kids = num(a.capacity);
      if (!kids) return null;
      // Georgia licensing minimums are ~35 sf per child of usable classroom
      // space; the whole building lands near 100-150 gsf per child.
      let low = kids * 95;
      let high = kids * 145;
      if (has(a.age_groups, 'infant')) {
        low += kids * 8;
        high += kids * 14;
      }
      if (has(a.childcare_spaces, 'gross-motor')) {
        low += 900;
        high += 1800;
      }
      if (has(a.childcare_spaces, 'kitchen')) {
        low += 300;
        high += 700;
      }
      return band(
        low,
        high,
        `${kids} children, including classrooms, circulation, and support. Confirm the per-child minimum with Georgia licensing.`,
        250,
      );
    },
  },

  veterinary: {
    tranches: [
      t('v1', 'Under 3,000 sf', 0, 3000, 'A small practice, two or three exam rooms'),
      t('v2', '3,000 – 6,000 sf', 3000, 6000, 'A typical companion animal hospital'),
      t('v3', '6,000 – 12,000 sf', 6000, 12000, 'A hospital with boarding and surgery'),
      t('v4', '12,000 – 25,000 sf', 12000, 25000, 'A referral or emergency hospital'),
      t('v5', 'Over 25,000 sf', 25000, 80000, 'A specialty campus or large shelter'),
    ],
    questions: [
      {
        id: 'practice_kind',
        label: 'What kind of practice?',
        type: 'select',
        required: true,
        options: [
          { value: 'companion', label: 'Companion animal general practice' },
          { value: 'emergency', label: 'Emergency and after-hours' },
          { value: 'specialty', label: 'Specialty or referral' },
          { value: 'equine', label: 'Equine or large animal' },
          { value: 'boarding', label: 'Boarding and daycare' },
          { value: 'shelter', label: 'Shelter or rescue' },
        ],
      },
      {
        id: 'exam_rooms',
        label: 'How many exam rooms?',
        type: 'number',
        min: 1,
        max: 40,
        step: 1,
        unit: 'rooms',
        required: true,
        help: 'This sets the size of the building the way seats set the size of a restaurant.',
      },
      {
        id: 'kennels',
        label: 'How many kennel or boarding runs?',
        type: 'number',
        min: 0,
        max: 400,
        step: 5,
        unit: 'runs',
        help: 'Boarding needs its own ventilation and drainage, so it adds more than its floor area suggests.',
      },
      {
        id: 'vet_spaces',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'surgery', label: 'Surgery suite' },
          { value: 'dental', label: 'Dental suite' },
          { value: 'imaging', label: 'X-ray or ultrasound' },
          { value: 'lab', label: 'In-house lab' },
          { value: 'isolation', label: 'Isolation ward' },
          { value: 'grooming', label: 'Grooming' },
          { value: 'daycare', label: 'Indoor daycare play' },
          { value: 'crematory', label: 'Crematory' },
        ],
      },
      {
        id: 'separate_flow',
        label: 'Do cats and dogs need separate paths?',
        type: 'boolean',
        help: 'Separate lobbies, wards, and corridors reduce stress and are increasingly expected. They also add area.',
      },
      look([
        { value: 'warm', label: 'Warm and calming for anxious animals' },
        { value: 'clinical', label: 'Clean and clinical' },
        { value: 'residential', label: 'Residential, fits a neighborhood' },
        { value: 'modern', label: 'Modern and distinctive' },
      ]),
    ],
    deriveSf: (a) => {
      const rooms = num(a.exam_rooms);
      if (!rooms) return null;
      const per: Record<string, [number, number]> = {
        companion: [750, 1050],
        emergency: [950, 1350],
        specialty: [1000, 1500],
        equine: [1200, 1900],
        boarding: [600, 900],
        shelter: [700, 1100],
      };
      const [lo, hi] = per[String(a.practice_kind)] ?? [750, 1050];
      let low = rooms * lo;
      let high = rooms * hi;

      const runs = Number(a.kennels) || 0;
      low += runs * 55;
      high += runs * 85;

      if (has(a.vet_spaces, 'surgery')) {
        low += 500;
        high += 900;
      }
      if (has(a.vet_spaces, 'isolation')) {
        low += 250;
        high += 500;
      }
      if (a.separate_flow === true) {
        low *= 1.08;
        high *= 1.15;
      }

      const parts = [`${rooms} exam ${rooms === 1 ? 'room' : 'rooms'}`];
      if (runs) parts.push(`${runs} boarding runs`);
      return band(low, high, `${parts.join(' and ')}, plus treatment, surgery, and lobby.`, 250);
    },
  },

  aviation: {
    tranches: [
      t('av1', 'Under 8,000 sf', 0, 8000, 'A single-bay hangar for a light aircraft'),
      t('av2', '8,000 – 20,000 sf', 8000, 20000, 'A corporate hangar for one or two jets'),
      t('av3', '20,000 – 45,000 sf', 20000, 45000, 'A multi-aircraft hangar with offices'),
      t('av4', '45,000 – 100,000 sf', 45000, 100000, 'An FBO or maintenance facility'),
      t('av5', 'Over 100,000 sf', 100000, 300000, 'A large MRO or airline facility'),
    ],
    questions: [
      {
        id: 'hangar_use',
        label: 'What is the building for?',
        type: 'select',
        required: true,
        options: [
          { value: 'corporate', label: 'Corporate hangar for our own aircraft' },
          { value: 'tenant', label: 'Hangars we will rent out' },
          { value: 'fbo', label: 'FBO with a terminal' },
          { value: 'mro', label: 'Maintenance and repair' },
          { value: 'tee', label: 'T-hangars for light aircraft' },
        ],
      },
      {
        id: 'largest_wingspan',
        label: 'How wide is the largest aircraft?',
        type: 'number',
        min: 25,
        max: 260,
        step: 5,
        unit: 'ft wingspan',
        required: true,
        help: 'The door has to be about 20 feet wider than the wingspan, and that sets the whole building.',
      },
      {
        id: 'tail_height',
        label: 'How tall is its tail?',
        type: 'select',
        help: 'Door height is tail height plus clearance, and door height drives the structure.',
        options: [
          { value: '18', label: 'Under 18 feet — light aircraft' },
          { value: '25', label: 'About 25 feet — midsize jets' },
          { value: '30', label: 'About 30 feet — large jets' },
          { value: '40', label: 'Over 40 feet' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'aircraft_count',
        label: 'How many aircraft need to fit inside at once?',
        type: 'number',
        min: 1,
        max: 60,
        step: 1,
        unit: 'aircraft',
      },
      {
        id: 'aviation_spaces',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'offices', label: 'Offices' },
          { value: 'lounge', label: 'Pilot and passenger lounge' },
          { value: 'shop', label: 'Maintenance shop' },
          { value: 'parts', label: 'Parts storage' },
          { value: 'paint', label: 'Paint or wash bay' },
          { value: 'fuel', label: 'Fuel farm' },
          { value: 'crew', label: 'Crew quarters' },
        ],
      },
      look([
        { value: 'clean-industrial', label: 'Clean industrial, all business' },
        { value: 'corporate', label: 'A corporate front that reflects the company' },
        { value: 'premium', label: 'Premium — clients arrive here' },
      ]),
    ],
    deriveSf: (a) => {
      const span = num(a.largest_wingspan);
      const count = Number(a.aircraft_count) || 1;
      if (!span) return null;
      // Hangar footprint scales with the square of the wingspan, plus tail
      // clearance and circulation between aircraft.
      const door = span + 20;
      const depth = span * 0.95 + 20;
      const bay = door * depth;
      let low = bay * (1 + (count - 1) * 0.75);
      let high = bay * (1 + (count - 1) * 0.95) * 1.15;
      if (has(a.aviation_spaces, 'offices')) {
        low += 1200;
        high += 3000;
      }
      if (has(a.aviation_spaces, 'shop')) {
        low += 1500;
        high += 4000;
      }
      if (has(a.aviation_spaces, 'lounge')) {
        low += 800;
        high += 2000;
      }
      return band(
        low,
        high,
        `A door about ${Math.round(door)} feet wide for a ${span}-foot wingspan, holding ${count} aircraft.`,
        500,
      );
    },
  },

  agricultural: {
    tranches: [
      t('ag1', 'Under 5,000 sf', 0, 5000, 'An equipment or hay barn'),
      t('ag2', '5,000 – 15,000 sf', 5000, 15000, 'A shop and equipment building'),
      t('ag3', '15,000 – 40,000 sf', 15000, 40000, 'A packing or processing building'),
      t('ag4', '40,000 – 100,000 sf', 40000, 100000, 'A greenhouse range or indoor grow'),
      t('ag5', 'Over 100,000 sf', 100000, 500000, 'A large controlled-environment facility'),
    ],
    questions: [
      {
        id: 'ag_use',
        label: 'What is the building for?',
        type: 'select',
        required: true,
        options: [
          { value: 'equipment', label: 'Equipment and implement storage' },
          { value: 'shop', label: 'Farm shop and repair' },
          { value: 'livestock', label: 'Livestock' },
          { value: 'processing', label: 'Packing or processing' },
          { value: 'greenhouse', label: 'Greenhouse' },
          { value: 'indoor-grow', label: 'Indoor controlled-environment growing' },
          { value: 'storage', label: 'Grain, hay, or commodity storage' },
        ],
      },
      {
        id: 'building_sf',
        label: 'About how much covered area do you need?',
        type: 'number',
        min: 1000,
        max: 500000,
        step: 1000,
        unit: 'sf',
        required: true,
        help: 'A rough footprint is fine. If you know the length and width, multiply them.',
      },
      {
        id: 'clear_span',
        label: 'How wide does it need to be, clear of columns?',
        type: 'select',
        help: 'Equipment turning radius usually decides this.',
        options: [
          { value: '40', label: 'About 40 feet' },
          { value: '60', label: 'About 60 feet' },
          { value: '80', label: 'About 80 feet' },
          { value: '100', label: '100 feet or more' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'sidewall',
        label: 'How tall do the sidewalls need to be?',
        type: 'select',
        options: [
          { value: '14', label: 'About 14 feet' },
          { value: '16', label: 'About 16 feet' },
          { value: '20', label: 'About 20 feet' },
          { value: '24', label: '24 feet or more' },
        ],
      },
      {
        id: 'ag_systems',
        label: 'What has to be built in?',
        type: 'multi',
        options: [
          { value: 'climate', label: 'Heating and cooling' },
          { value: 'humidity', label: 'Humidity control' },
          { value: 'lighting', label: 'Grow lighting' },
          { value: 'irrigation', label: 'Irrigation and water treatment' },
          { value: 'cold', label: 'Refrigerated or cold storage' },
          { value: 'washdown', label: 'Washdown and floor drains' },
          { value: 'power', label: 'Heavy electrical service' },
          { value: 'none', label: 'An unconditioned shell' },
        ],
      },
      look([
        { value: 'utilitarian', label: 'Purely functional' },
        { value: 'farmstead', label: 'Farmstead character, fits the property' },
        { value: 'agritourism', label: 'Customers will visit — it needs to look good' },
      ]),
    ],
    deriveSf: (a) => {
      const sf = num(a.building_sf);
      if (!sf) return null;
      // Support space is small on a barn and substantial on a processing plant.
      const overhead: Record<string, [number, number]> = {
        equipment: [1.0, 1.05],
        shop: [1.05, 1.15],
        livestock: [1.0, 1.1],
        processing: [1.15, 1.35],
        greenhouse: [1.02, 1.12],
        'indoor-grow': [1.2, 1.45],
        storage: [1.0, 1.05],
      };
      const [lo, hi] = overhead[String(a.ag_use)] ?? [1.05, 1.2];
      return band(sf * lo, sf * hi, `About ${sf.toLocaleString()} sf of covered area, plus support space.`, 500);
    },
  },

  utility: {
    tranches: [
      t('u1', 'Under 2,000 sf', 0, 2000, 'A substation control building'),
      t('u2', '2,000 – 6,000 sf', 2000, 6000, 'A control house or a small EV hub'),
      t('u3', '6,000 – 15,000 sf', 6000, 15000, 'An operations building or battery enclosure'),
      t('u4', '15,000 – 40,000 sf', 15000, 40000, 'A utility operations center'),
      t('u5', 'Over 40,000 sf', 40000, 150000, 'A large energy or storage facility'),
    ],
    questions: [
      {
        id: 'utility_kind',
        label: 'What kind of facility?',
        type: 'select',
        required: true,
        options: [
          { value: 'control', label: 'Substation control building' },
          { value: 'ops', label: 'Utility operations and crew building' },
          { value: 'ev', label: 'EV charging hub' },
          { value: 'bess', label: 'Battery energy storage' },
          { value: 'switchgear', label: 'Switchgear or electrical room building' },
          { value: 'water', label: 'Water or wastewater' },
        ],
      },
      {
        id: 'equipment_sf',
        label: 'How much floor area does the equipment need?',
        type: 'number',
        min: 200,
        max: 100000,
        step: 100,
        unit: 'sf',
        help: 'If your supplier has given you a layout, use that footprint.',
      },
      {
        id: 'charging_stalls',
        label: 'How many charging stalls?',
        type: 'number',
        min: 0,
        max: 200,
        step: 2,
        unit: 'stalls',
        showIf: { questionId: 'utility_kind', equals: 'ev' },
        help: 'Cabinets, transformers, and switchgear scale with this, and they need their own pad and screening.',
      },
      {
        id: 'redundancy',
        label: 'How much redundancy do you need?',
        type: 'select',
        options: [
          { value: 'n', label: 'None — one of everything' },
          { value: 'n1', label: 'One spare of each critical item' },
          { value: '2n', label: 'Fully duplicated' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'utility_needs',
        label: 'What does the building have to handle?',
        type: 'multi',
        options: [
          { value: 'blast', label: 'Blast or arc-flash resistance' },
          { value: 'storm', label: 'Severe storm and wind' },
          { value: 'acoustic', label: 'Noise control for neighbors' },
          { value: 'fire', label: 'Special fire suppression' },
          { value: 'crew', label: 'Crew space, restrooms, and lockers' },
          { value: 'unmanned', label: 'Normally unmanned' },
          { value: 'screening', label: 'Screening so it is not an eyesore' },
        ],
      },
      look([
        { value: 'invisible', label: 'As unobtrusive as possible' },
        { value: 'civic', label: 'Presentable — the public sees it' },
        { value: 'utilitarian', label: 'Purely functional' },
      ]),
    ],
    deriveSf: (a) => {
      const kind = String(a.utility_kind);
      if (kind === 'ev') {
        const stalls = num(a.charging_stalls);
        if (!stalls) return null;
        // The building is small; the equipment yard and canopy are the project.
        return band(
          400 + stalls * 45,
          900 + stalls * 90,
          `${stalls} charging stalls, with switchgear and equipment space. Most of this project is the site, not the building.`,
          100,
        );
      }
      const sf = num(a.equipment_sf);
      if (!sf) return null;
      const overhead = a.redundancy === '2n' ? [1.6, 2.1] : a.redundancy === 'n1' ? [1.35, 1.7] : [1.2, 1.5];
      let low = sf * overhead[0];
      let high = sf * overhead[1];
      if (has(a.utility_needs, 'crew')) {
        low += 600;
        high += 1500;
      }
      return band(low, high, `About ${sf.toLocaleString()} sf of equipment, plus clearances and access.`, 100);
    },
  },

  convenience: {
    tranches: [
      t('cv1', 'Under 3,000 sf', 0, 3000, 'A small store with a fuel canopy'),
      t('cv2', '3,000 – 5,500 sf', 3000, 5500, 'A typical new convenience store'),
      t('cv3', '5,500 – 9,000 sf', 5500, 9000, 'A large store with a food program'),
      t('cv4', '9,000 – 20,000 sf', 9000, 20000, 'A travel center'),
      t('cv5', 'Over 20,000 sf', 20000, 60000, 'A truck stop with restaurants and showers'),
    ],
    questions: [
      {
        id: 'store_type',
        label: 'What kind of store?',
        type: 'select',
        required: true,
        options: [
          { value: 'cstore', label: 'Convenience store with fuel' },
          { value: 'nofuel', label: 'Convenience store, no fuel' },
          { value: 'travel', label: 'Travel center' },
          { value: 'truckstop', label: 'Truck stop' },
          { value: 'ev-first', label: 'EV charging with a store' },
        ],
      },
      {
        id: 'fuel_positions',
        label: 'How many fueling positions?',
        type: 'number',
        min: 0,
        max: 40,
        step: 2,
        unit: 'positions',
        help: 'One dispenser with hoses on both sides is two positions. Enter 0 if there is no fuel.',
      },
      {
        id: 'food_program',
        label: 'How much food service?',
        type: 'select',
        help: 'This is what decides the size of the building more than the fuel does.',
        options: [
          { value: 'none', label: 'Packaged only' },
          { value: 'coffee', label: 'Coffee and roller grill' },
          { value: 'made-to-order', label: 'Made-to-order kitchen' },
          { value: 'qsr', label: 'A branded quick service restaurant inside' },
          { value: 'multiple', label: 'Several food brands' },
        ],
      },
      {
        id: 'seating',
        label: 'Will there be seating?',
        type: 'number',
        min: 0,
        max: 200,
        step: 4,
        unit: 'seats',
      },
      {
        id: 'cstore_features',
        label: 'What else is on the site?',
        type: 'multi',
        options: [
          { value: 'carwash', label: 'Car wash' },
          { value: 'ev', label: 'EV fast charging' },
          { value: 'diesel', label: 'Truck diesel lanes' },
          { value: 'showers', label: 'Driver showers and lounge' },
          { value: 'beer-cave', label: 'Walk-in beer cave' },
          { value: 'propane', label: 'Propane exchange' },
          { value: 'atm', label: 'ATM and lottery' },
          { value: 'canopy', label: 'Fuel canopy' },
        ],
      },
      look([
        { value: 'brand', label: 'Our brand’s standard building' },
        { value: 'upscale', label: 'Upscale — closer to a market than a gas station' },
        { value: 'regional', label: 'Regional character' },
        { value: 'simple', label: 'Simple and clean' },
      ]),
    ],
    deriveSf: (a) => {
      if (!a.store_type) return null;
      const type = String(a.store_type);
      const base: Record<string, [number, number]> = {
        cstore: [3000, 5000],
        nofuel: [2400, 4200],
        travel: [7000, 12000],
        truckstop: [10000, 20000],
        'ev-first': [2800, 5000],
      };
      const [lo, hi] = base[type] ?? [3000, 5000];
      let low = lo;
      let high = hi;

      const food: Record<string, [number, number]> = {
        none: [0, 0],
        coffee: [150, 350],
        'made-to-order': [500, 900],
        qsr: [900, 1600],
        multiple: [1600, 3000],
      };
      const [fl, fh] = food[String(a.food_program)] ?? [0, 0];
      low += fl;
      high += fh;

      const seats = Number(a.seating) || 0;
      low += seats * 16;
      high += seats * 24;

      if (has(a.cstore_features, 'showers')) {
        low += 1200;
        high += 3000;
      }

      const positions = Number(a.fuel_positions) || 0;
      const note = positions
        ? `A store with ${positions} fueling positions under a canopy.`
        : 'A store with no fuel.';
      return band(low, high, `${note} The canopy and site are sized separately.`, 250);
    },
  },

  bank: {
    tranches: [
      t('b1', 'Under 2,500 sf', 0, 2500, 'A small in-line or express branch'),
      t('b2', '2,500 – 4,500 sf', 2500, 4500, 'A typical freestanding branch'),
      t('b3', '4,500 – 8,000 sf', 4500, 8000, 'A branch with regional offices above'),
      t('b4', '8,000 – 25,000 sf', 8000, 25000, 'A headquarters or operations center'),
      t('b5', 'Over 25,000 sf', 25000, 100000, 'A corporate campus'),
    ],
    questions: [
      {
        id: 'branch_type',
        label: 'What kind of facility?',
        type: 'select',
        required: true,
        options: [
          { value: 'branch', label: 'Freestanding branch' },
          { value: 'inline', label: 'In-line branch in a retail center' },
          { value: 'express', label: 'Express or micro branch' },
          { value: 'ops', label: 'Operations center' },
          { value: 'hq', label: 'Headquarters' },
        ],
      },
      {
        id: 'staff',
        label: 'How many people work here?',
        type: 'number',
        min: 2,
        max: 500,
        step: 1,
        unit: 'staff',
        required: true,
      },
      {
        id: 'drive_lanes',
        label: 'How many drive-up lanes?',
        type: 'number',
        min: 0,
        max: 8,
        step: 1,
        unit: 'lanes',
        help: 'Include the ATM lane. Stacking depth affects the site more than the building.',
      },
      {
        id: 'bank_spaces',
        label: 'What is inside?',
        type: 'multi',
        options: [
          { value: 'teller', label: 'Traditional teller line' },
          { value: 'pods', label: 'Universal banker pods' },
          { value: 'offices', label: 'Private offices for lending' },
          { value: 'conference', label: 'Conference room' },
          { value: 'vault', label: 'Vault' },
          { value: 'safe-deposit', label: 'Safe deposit boxes' },
          { value: 'atm-vestibule', label: 'After-hours ATM vestibule' },
          { value: 'community', label: 'A community room the public can book' },
        ],
      },
      {
        id: 'security_needs',
        label: 'What does security require?',
        type: 'multi',
        options: [
          { value: 'ballistic', label: 'Ballistic protection at the teller line' },
          { value: 'cameras', label: 'Full camera coverage' },
          { value: 'alarm', label: 'Monitored alarm and duress' },
          { value: 'lighting', label: 'Site lighting to a security standard' },
          { value: 'standard', label: 'Standard commercial security' },
        ],
      },
      look([
        { value: 'traditional', label: 'Traditional and solid — it should feel permanent' },
        { value: 'modern-retail', label: 'Modern retail, approachable and open' },
        { value: 'community', label: 'Local and community-rooted' },
        { value: 'corporate', label: 'Corporate and polished' },
      ]),
    ],
    deriveSf: (a) => {
      const staff = num(a.staff);
      if (!staff) return null;
      const per: Record<string, [number, number]> = {
        branch: [280, 420],
        inline: [200, 320],
        express: [160, 260],
        ops: [220, 320],
        hq: [260, 400],
      };
      const [lo, hi] = per[String(a.branch_type)] ?? [260, 400];
      let low = Math.max(staff * lo, 1400);
      let high = Math.max(staff * hi, 2400);
      if (has(a.bank_spaces, 'vault')) {
        low += 250;
        high += 600;
      }
      if (has(a.bank_spaces, 'community')) {
        low += 400;
        high += 900;
      }
      const lanes = Number(a.drive_lanes) || 0;
      if (lanes > 0) {
        low += 120;
        high += 300;
      }
      return band(low, high, `${staff} staff, plus the lobby, offices, and back of house.`, 250);
    },
  },

  laboratory: {
    tranches: [
      t('l1', 'Under 6,000 sf', 0, 6000, 'A small testing or QC lab'),
      t('l2', '6,000 – 15,000 sf', 6000, 15000, 'A single-tenant research lab'),
      t('l3', '15,000 – 40,000 sf', 15000, 40000, 'A research building with offices'),
      t('l4', '40,000 – 100,000 sf', 40000, 100000, 'A multi-tenant life sciences building'),
      t('l5', 'Over 100,000 sf', 100000, 400000, 'A research campus'),
    ],
    questions: [
      {
        id: 'lab_type',
        label: 'What kind of lab?',
        type: 'select',
        required: true,
        options: [
          { value: 'wet', label: 'Wet lab — chemistry or biology' },
          { value: 'dry', label: 'Dry lab — electronics, computing, metrology' },
          { value: 'testing', label: 'Materials or product testing' },
          { value: 'qc', label: 'Quality control for a production plant' },
          { value: 'pilot', label: 'Pilot plant or scale-up' },
          { value: 'cleanroom', label: 'Cleanroom manufacturing' },
        ],
      },
      {
        id: 'bench_modules',
        label: 'How many bench modules or lab stations?',
        type: 'number',
        min: 1,
        max: 300,
        step: 1,
        unit: 'modules',
        required: true,
        help: 'A module is one researcher’s bench and the aisle beside it. If you are not sure, count the people who need a bench.',
      },
      {
        id: 'biosafety',
        label: 'What biosafety level?',
        type: 'select',
        help: 'This drives ventilation, containment, and cost more than the size does.',
        options: [
          { value: 'none', label: 'Not applicable' },
          { value: 'bsl1', label: 'BSL-1' },
          { value: 'bsl2', label: 'BSL-2' },
          { value: 'bsl3', label: 'BSL-3' },
          { value: 'unsure', label: 'Not sure yet' },
        ],
      },
      {
        id: 'lab_support',
        label: 'What support space is needed?',
        type: 'multi',
        options: [
          { value: 'fume', label: 'Fume hoods' },
          { value: 'cold', label: 'Cold rooms and freezers' },
          { value: 'glass', label: 'Glasswash and autoclave' },
          { value: 'chemical', label: 'Chemical storage' },
          { value: 'gas', label: 'Specialty gas' },
          { value: 'vivarium', label: 'Animal facility' },
          { value: 'cleanroom', label: 'Cleanroom' },
          { value: 'offices', label: 'Offices and write-up space' },
        ],
      },
      {
        id: 'office_ratio',
        label: 'How much office space alongside the lab?',
        type: 'select',
        options: [
          { value: 'minimal', label: 'Minimal — write-up desks in the lab' },
          { value: 'balanced', label: 'About a third office' },
          { value: 'office-heavy', label: 'Half or more office' },
        ],
      },
      look([
        { value: 'research', label: 'Serious and research-forward' },
        { value: 'showcase', label: 'A showcase — investors and clients visit' },
        { value: 'industrial', label: 'Industrial and practical' },
        { value: 'campus', label: 'Fits an existing campus' },
      ]),
    ],
    deriveSf: (a) => {
      const modules = num(a.bench_modules);
      if (!modules) return null;
      const per: Record<string, [number, number]> = {
        wet: [260, 360],
        dry: [180, 260],
        testing: [220, 320],
        qc: [200, 300],
        pilot: [350, 550],
        cleanroom: [320, 480],
      };
      const [lo, hi] = per[String(a.lab_type)] ?? [240, 340];
      let low = modules * lo;
      let high = modules * hi;

      const supportFactor: Record<string, [number, number]> = {
        minimal: [1.35, 1.6],
        balanced: [1.6, 1.95],
        'office-heavy': [1.9, 2.4],
      };
      const [sl, sh] = supportFactor[String(a.office_ratio)] ?? [1.6, 1.95];
      low *= sl;
      high *= sh;

      if (has(a.lab_support, 'vivarium')) {
        low += 2000;
        high += 5000;
      }
      if (has(a.lab_support, 'cleanroom')) {
        low += 1500;
        high += 4000;
      }
      if (a.biosafety === 'bsl3') {
        low *= 1.15;
        high *= 1.35;
      }

      return band(low, high, `${modules} bench modules, plus lab support, offices, and mechanical space.`, 500);
    },
  },

  funeral: {
    tranches: [
      t('f1', 'Under 4,500 sf', 0, 4500, 'A small funeral home without a chapel'),
      t('f2', '4,500 – 9,000 sf', 4500, 9000, 'A typical funeral home with a chapel'),
      t('f3', '9,000 – 15,000 sf', 9000, 15000, 'A large home with multiple visitation rooms'),
      t('f4', '15,000 – 30,000 sf', 15000, 30000, 'A funeral home with a crematory and reception'),
      t('f5', 'Over 30,000 sf', 30000, 80000, 'A combined funeral home and cemetery facility'),
    ],
    questions: [
      {
        id: 'services_per_week',
        label: 'How many services will you hold in a typical week?',
        type: 'number',
        min: 1,
        max: 40,
        step: 1,
        unit: 'services',
        required: true,
        help: 'This decides how many visitation rooms you need running at once.',
      },
      {
        id: 'chapel_seats',
        label: 'How many seats in the chapel?',
        type: 'number',
        min: 0,
        max: 600,
        step: 10,
        unit: 'seats',
        help: 'Enter 0 if services are held at churches rather than here.',
      },
      {
        id: 'visitation_rooms',
        label: 'How many visitation rooms?',
        type: 'number',
        min: 0,
        max: 10,
        step: 1,
        unit: 'rooms',
      },
      {
        id: 'funeral_spaces',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'prep', label: 'Preparation room' },
          { value: 'crematory', label: 'Crematory' },
          { value: 'reception', label: 'Reception or fellowship space' },
          { value: 'kitchen', label: 'Catering kitchen' },
          { value: 'selection', label: 'Casket and urn selection room' },
          { value: 'arrangement', label: 'Arrangement offices' },
          { value: 'garage', label: 'Enclosed garage for coaches' },
          { value: 'porte', label: 'Covered entry for families' },
        ],
      },
      {
        id: 'arrival',
        label: 'How do families arrive?',
        type: 'select',
        help: 'Separating the family arrival from the operational entrance matters more here than in most buildings.',
        options: [
          { value: 'porte-separate', label: 'Covered entry, with a separate service entrance out of sight' },
          { value: 'single', label: 'One main entrance' },
          { value: 'campus', label: 'Part of a cemetery or campus' },
        ],
      },
      look([
        { value: 'traditional', label: 'Traditional and residential, columns and brick' },
        { value: 'contemporary', label: 'Contemporary and calm' },
        { value: 'chapel', label: 'Chapel-like and reverent' },
        { value: 'estate', label: 'Estate-like, set in landscaping' },
      ]),
    ],
    deriveSf: (a) => {
      const services = num(a.services_per_week);
      if (!services) return null;
      const seats = Number(a.chapel_seats) || 0;
      const rooms = Number(a.visitation_rooms) || Math.max(1, Math.ceil(services / 4));

      let low = 2200;
      let high = 3600;
      low += seats * 13;
      high += seats * 19;
      low += rooms * 700;
      high += rooms * 1100;

      if (has(a.funeral_spaces, 'prep')) {
        low += 350;
        high += 650;
      }
      if (has(a.funeral_spaces, 'crematory')) {
        low += 500;
        high += 1000;
      }
      if (has(a.funeral_spaces, 'reception')) {
        low += 900;
        high += 2000;
      }
      if (has(a.funeral_spaces, 'garage')) {
        low += 700;
        high += 1400;
      }

      return band(
        low,
        high,
        `${rooms} visitation ${rooms === 1 ? 'room' : 'rooms'}${seats ? ` and a ${seats}-seat chapel` : ''}, plus offices and preparation space.`,
        250,
      );
    },
  },

  flex: {
    tranches: [
      t('fx1', 'Under 10,000 sf', 0, 10000, 'Four to six small contractor bays'),
      t('fx2', '10,000 – 25,000 sf', 10000, 25000, 'A single small-bay flex building'),
      t('fx3', '25,000 – 60,000 sf', 25000, 60000, 'A flex building or two'),
      t('fx4', '60,000 – 120,000 sf', 60000, 120000, 'A small business park'),
      t('fx5', 'Over 120,000 sf', 120000, 400000, 'A multi-building flex park'),
    ],
    questions: [
      {
        id: 'flex_model',
        label: 'Who will use the building?',
        type: 'select',
        required: true,
        options: [
          { value: 'lease', label: 'Tenants we lease to' },
          { value: 'condo', label: 'Owners who buy their own bay' },
          { value: 'own-use', label: 'Our own business' },
          { value: 'incubator', label: 'Small businesses and startups' },
        ],
      },
      {
        id: 'bays',
        label: 'How many bays?',
        type: 'number',
        min: 1,
        max: 100,
        step: 1,
        unit: 'bays',
        required: true,
      },
      {
        id: 'bay_size',
        label: 'How big is each bay?',
        type: 'select',
        help: 'Bay width times building depth. Most small-bay flex is 25 or 30 feet wide.',
        options: [
          { value: '1500', label: 'About 1,500 sf' },
          { value: '2500', label: 'About 2,500 sf' },
          { value: '4000', label: 'About 4,000 sf' },
          { value: '6000', label: 'About 6,000 sf' },
          { value: '10000', label: '10,000 sf or larger' },
        ],
      },
      {
        id: 'office_finish',
        label: 'How much of each bay is finished office?',
        type: 'select',
        options: [
          { value: 'shell', label: 'None — a shell with a restroom' },
          { value: 'low', label: 'About 15 percent' },
          { value: 'medium', label: 'About 30 percent' },
          { value: 'high', label: 'Half or more' },
        ],
      },
      {
        id: 'flex_features',
        label: 'What does each bay need?',
        type: 'multi',
        options: [
          { value: 'grade', label: 'A drive-in door at grade' },
          { value: 'dock', label: 'A dock-high door' },
          { value: 'storefront', label: 'Storefront glass at the front' },
          { value: 'mezzanine', label: 'Room for a mezzanine' },
          { value: 'three-phase', label: 'Three-phase power' },
          { value: 'separate-meters', label: 'Separate utility meters per bay' },
          { value: 'yard', label: 'Fenced yard space' },
        ],
      },
      look([
        { value: 'clean', label: 'Clean and modern, attracts better tenants' },
        { value: 'utilitarian', label: 'Straightforward and economical' },
        { value: 'retail-front', label: 'A retail-quality front elevation' },
      ]),
    ],
    deriveSf: (a) => {
      const bays = num(a.bays);
      const size = Number(a.bay_size);
      if (!bays || !Number.isFinite(size) || size <= 0) return null;
      const gross = bays * size;
      return band(
        gross * 1.02,
        gross * 1.1,
        `${bays} ${bays === 1 ? 'bay' : 'bays'} at about ${size.toLocaleString()} sf each, plus common walls and utility space.`,
        500,
      );
    },
  },

  museum: {
    tranches: [
      t('ms1', 'Under 8,000 sf', 0, 8000, 'A visitor center or small gallery'),
      t('ms2', '8,000 – 20,000 sf', 8000, 20000, 'A local history or community museum'),
      t('ms3', '20,000 – 50,000 sf', 20000, 50000, 'A regional museum with programs'),
      t('ms4', '50,000 – 120,000 sf', 50000, 120000, 'A major museum or performing arts center'),
      t('ms5', 'Over 120,000 sf', 120000, 400000, 'A large cultural institution'),
    ],
    questions: [
      {
        id: 'institution_type',
        label: 'What kind of institution?',
        type: 'select',
        required: true,
        options: [
          { value: 'history', label: 'History or heritage museum' },
          { value: 'art', label: 'Art museum or gallery' },
          { value: 'science', label: 'Science or children’s museum' },
          { value: 'performing', label: 'Performing arts' },
          { value: 'visitor', label: 'Visitor or interpretive center' },
          { value: 'archive', label: 'Archive or special collections' },
        ],
      },
      {
        id: 'gallery_sf',
        label: 'How much exhibit or performance space?',
        type: 'number',
        min: 500,
        max: 200000,
        step: 500,
        unit: 'sf',
        required: true,
        help: 'Just the space visitors experience. We will add the support space around it.',
      },
      {
        id: 'seats',
        label: 'How many seats in the theater or auditorium?',
        type: 'number',
        min: 0,
        max: 3000,
        step: 25,
        unit: 'seats',
        help: 'Leave at 0 if there is no performance space.',
      },
      {
        id: 'collection_care',
        label: 'Do you hold a collection?',
        type: 'select',
        help: 'Collections need climate control, security, and storage that costs more per square foot than the galleries.',
        options: [
          { value: 'none', label: 'No permanent collection' },
          { value: 'modest', label: 'A modest collection' },
          { value: 'significant', label: 'A significant collection needing conservation' },
          { value: 'loans', label: 'We borrow works and must meet lender standards' },
        ],
      },
      {
        id: 'museum_spaces',
        label: 'What else is in the building?',
        type: 'multi',
        options: [
          { value: 'education', label: 'Classrooms and education space' },
          { value: 'event', label: 'Rentable event space' },
          { value: 'cafe', label: 'Café' },
          { value: 'shop', label: 'Museum shop' },
          { value: 'storage', label: 'Collection storage' },
          { value: 'conservation', label: 'Conservation lab' },
          { value: 'loading', label: 'Secure loading and crate handling' },
          { value: 'offices', label: 'Staff offices' },
        ],
      },
      look([
        { value: 'landmark', label: 'A landmark — the building is part of the draw' },
        { value: 'contextual', label: 'Contextual, deferring to what is around it' },
        { value: 'restrained', label: 'Restrained, so the collection is the subject' },
        { value: 'adaptive', label: 'Adaptive reuse of an existing building' },
      ]),
    ],
    deriveSf: (a) => {
      const gallery = num(a.gallery_sf);
      if (!gallery) return null;
      // Public-facing space is roughly 40-50% of a museum; the rest is
      // storage, conservation, mechanical, and staff.
      const ratio: Record<string, [number, number]> = {
        none: [1.6, 2.0],
        modest: [1.9, 2.4],
        significant: [2.2, 2.9],
        loans: [2.3, 3.0],
      };
      const [lo, hi] = ratio[String(a.collection_care)] ?? [1.9, 2.4];
      let low = gallery * lo;
      let high = gallery * hi;

      const seats = Number(a.seats) || 0;
      low += seats * 22;
      high += seats * 34;

      if (has(a.museum_spaces, 'cafe')) {
        low += 800;
        high += 2000;
      }
      if (has(a.museum_spaces, 'conservation')) {
        low += 1000;
        high += 2500;
      }

      return band(
        low,
        high,
        `${gallery.toLocaleString()} sf of visitor space${seats ? ` and a ${seats}-seat theater` : ''}, plus storage, support, and staff areas.`,
        500,
      );
    },
  },
};
